import { useState } from 'react';
import { MapPin, Copy, Check, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CopyableCoordinatesProps {
  latitude: number | null;
  longitude: number | null;
  geohash?: string | null;
  /** When true, renders without Card wrapper for embedding in other cards */
  embedded?: boolean;
}

export function CopyableCoordinates({
  latitude,
  longitude,
  geohash,
  embedded = false,
}: CopyableCoordinatesProps): JSX.Element | null {
  const [copied, setCopied] = useState<'coords' | 'geohash' | null>(null);

  if (latitude === null || longitude === null) {
    return null;
  }

  const latStr = latitude.toFixed(6);
  const lngStr = longitude.toFixed(6);
  const coordsString = `${latStr}, ${lngStr}`;
  const googleMapsUrl = `https://www.google.com/maps?q=${latStr},${lngStr}`;

  const handleCopy = async (text: string, type: 'coords' | 'geohash'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => {
        setCopied(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const content = (
    <TooltipProvider>
      <div className="space-y-3">
        {/* Coordinates */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-muted-foreground">Lat/Long</span>
            <p className="font-mono text-sm">{coordsString}</p>
          </div>
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void handleCopy(coordsString, 'coords');
                  }}
                >
                  {copied === 'coords' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {copied === 'coords' ? 'Copied!' : 'Copy coordinates'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent>Open in Google Maps</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Geohash */}
        {geohash && (
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <span className="text-sm text-muted-foreground">Geohash</span>
              <p className="font-mono text-sm">{geohash}</p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void handleCopy(geohash, 'geohash');
                  }}
                >
                  {copied === 'geohash' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {copied === 'geohash' ? 'Copied!' : 'Copy geohash'}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </TooltipProvider>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Coordinates
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
